import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link2, Pencil } from "lucide-react";
import Modal from "../ui/Modal";
import { ParsingManageHotelUrls } from "../../api/parsing";
import { HotelMappingRow } from "../../types/hotels";

export type EditablePlatformKey = keyof ParsingManageHotelUrls;

type EditablePlatformField = {
  key: EditablePlatformKey;
  label: string;
  description?: string;
};

export const EDITABLE_PLATFORM_FIELDS: EditablePlatformField[] = [
  { key: "official_website", label: "Сайт конкурента", description: "Official website / TravelLine" },
  { key: "ostrovok", label: "Островок" },
  { key: "t_travel", label: "Т-Путешествия" },
  { key: "yandex_travel", label: "Яндекс Путешествия" },
  { key: "sutochno", label: "Суточно" },
  { key: "alean", label: "Alean" },
];

type FormValues = Record<EditablePlatformKey, string>;

type Props = {
  open: boolean;
  row: HotelMappingRow | null;
  initialUrls: Partial<Record<EditablePlatformKey, string>>;
  onClose: () => void;
  onSave: (
    rowId: number,
    urls: Partial<Record<EditablePlatformKey, string | null>>,
    touchedPlatforms: EditablePlatformKey[],
  ) => void;
};

const EMPTY_VALUES: FormValues = {
  official_website: "",
  ostrovok: "",
  t_travel: "",
  yandex_travel: "",
  sutochno: "",
  alean: "",
};

export default function EditMappingModal({ open, row, initialUrls, onClose, onSave }: Props) {
  const [formValues, setFormValues] = useState<FormValues>(EMPTY_VALUES);
  const [error, setError] = useState("");
  const baseInputClass =
    "w-full rounded-2xl border border-slate-200/80 bg-white/80 px-4 py-[14px] text-[15px] text-slate-900 placeholder:text-slate-400 shadow-inner transition focus:border-indigo-300 focus:ring-4 focus:ring-indigo-200/60";

  const normalizedInitialValues = useMemo<FormValues>(
    () => ({
      official_website: initialUrls.official_website ?? "",
      ostrovok: initialUrls.ostrovok ?? "",
      t_travel: initialUrls.t_travel ?? "",
      yandex_travel: initialUrls.yandex_travel ?? "",
      sutochno: initialUrls.sutochno ?? "",
      alean: initialUrls.alean ?? "",
    }),
    [initialUrls],
  );

  useEffect(() => {
    if (!open || !row) {
      setFormValues(EMPTY_VALUES);
      setError("");
      return;
    }

    setFormValues(normalizedInitialValues);
    setError("");
  }, [open, row, normalizedInitialValues]);

  if (!row) return null;

  function handleChange(key: EditablePlatformKey, value: string) {
    setFormValues((prev) => ({
      ...prev,
      [key]: value,
    }));
    setError("");
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!row) return;

    for (const field of EDITABLE_PLATFORM_FIELDS) {
      const value = formValues[field.key].trim();
      if (value && !/^https?:\/\//i.test(value)) {
        setError(`Поле «${field.label}» должно начинаться с http:// или https://`);
        return;
      }
    }

    const touchedPlatforms = EDITABLE_PLATFORM_FIELDS
      .filter((field) => normalizedInitialValues[field.key] !== formValues[field.key].trim())
      .map((field) => field.key);

    const nextUrls = EDITABLE_PLATFORM_FIELDS.reduce<Partial<Record<EditablePlatformKey, string | null>>>(
      (acc, field) => {
        const trimmed = formValues[field.key].trim();
        acc[field.key] = trimmed || null;
        return acc;
      },
      {},
    );

    onSave(row.id, nextUrls, touchedPlatforms);
    onClose();
  }

  return (
    <Modal open={open} onClose={onClose} title="" widthClass="max-w-4xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="text-center space-y-3 animate-softFade">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-100 to-sky-100 text-indigo-600 shadow-inner">
            <Pencil size={24} />
          </div>
          <div>
            <h3 className="text-2xl font-semibold text-slate-900">Редактировать маппинг</h3>
            <p className="mt-1 text-sm text-slate-500">
              Изменения сохраняются только в контексте текущей страницы и будут учтены при отправке в парсинг.
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-white/70 bg-slate-50/80 px-4 py-3 text-sm text-slate-600 shadow-inner">
          <div className="font-semibold text-slate-800">{row.name || "Без названия"}</div>
          <a
            href={row.url}
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-flex break-all text-indigo-500 hover:underline"
          >
            {row.url}
          </a>
        </div>

        <div className="rounded-2xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-800">
          Оставьте поле пустым, если ссылку нужно убрать из маппинга для текущей сессии.
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {EDITABLE_PLATFORM_FIELDS.map((field) => (
            <label key={field.key} className="block space-y-2">
              <span className="text-sm font-semibold text-slate-600">{field.label}</span>
              {field.description ? <span className="block text-xs text-slate-400">{field.description}</span> : null}
              <div className="relative">
                <Link2 size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={formValues[field.key]}
                  onChange={(event) => handleChange(field.key, event.target.value)}
                  className={`${baseInputClass} pl-11 pr-4`}
                  placeholder="https://…"
                />
              </div>
            </label>
          ))}
        </div>

        {error ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-800">
            {error}
          </div>
        ) : null}

        <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-center">
          <button
            type="submit"
            className="inline-flex flex-1 items-center justify-center rounded-2xl bg-gradient-to-r from-indigo-600 to-sky-600 px-6 py-3 text-sm font-semibold text-white shadow-[0_18px_30px_rgba(59,130,246,0.28)] transition hover:translate-y-[-1px] hover:brightness-105 sm:flex-none"
          >
            Сохранить
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex flex-1 items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 sm:flex-none"
          >
            Отмена
          </button>
        </div>
      </form>
    </Modal>
  );
}
